import {
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ArrowUpOnSquareIcon,
  ClockIcon,
  EyeSlashIcon,
  PlusCircleIcon,
  ShoppingCartIcon,
} from '@heroicons/react/20/solid';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import {
  BellAlertIcon,
  DevicePhoneMobileIcon,
  InboxIcon,
  MegaphoneIcon,
  PauseCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import React, {
  type ClipboardEventHandler,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';
import { twMerge } from 'tailwind-merge';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingIcon } from '@/components/UI/LoadingIcon';
import SoundPlayer from '@/components/UI/SoundPlayer';
import Header from '@/renderer/Header';
import { request } from '@/renderer/utils/api';
import { createBatcher } from '@/renderer/utils/coalesce';
import { useMediaQuery } from '@/renderer/utils/hooks/useMediaQuery';
import { isIosNative } from '@/renderer/utils/isIosNative';
import { isNativePlatform } from '@/renderer/utils/isNativePlatform';
import { useLocale } from '@/renderer/utils/locale';
// import { useIdleTimerContext } from 'react-idle-timer';
import useMe from '@/renderer/utils/useMe';
import { useMemberships } from '@/renderer/utils/useMemberships';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import { ClientOnly } from '@/components/ClientOnly';
import { useNextInfiniteRequest } from '@/renderer/utils/api/infiniteStore';
import { PollingDelaysDefault } from '@/renderer/utils/api/types';
import { useAddToHomescreenPrompt } from '@/renderer/utils/hooks/useAddToHomescreenPrompt';
import Alert from './Alert';
import ConnectionStatusIcon from './ConnectionStatusIcon';
import LiveNowIndicator from './LiveNowIndicator';
import Message from './Message';
import MessageInputSkeleton from './MessageInputSkeleton';
import MessageReceiptLogger from './MessageReceiptLogger';
import { PendingPicksNotice } from './PendingPicksNotice';
import Release from './Release';
import SkeletonMessages from './SkeletonMessages';
import { isSetupMessage } from './setupConfirm';

const messageCreateRequest: RequestSchema<
  API.DirectMessageReceipt,
  API.WithData<{ content: string; receiverUserId: API.Id | '0'; type: 'dm' | 'dmh' }>
> = {
  name: 'messageCreateRequest',
  input: '/api/v1/message/create',
  method: 'POST',
};

// Delivery-reach checks for the "don't miss a release" notice: whether the member
// has a live Telegram link and/or a live native push registration. The notice is
// hidden once EITHER is present (they're reachable). Same endpoints the Settings
// Telegram + Push panels use.
const telegramStatusRequest: RequestSchema<{ connected: boolean }> = {
  name: 'telegramStatus',
  input: '/api/v1/telegram',
  method: 'GET',
};

const pushStatusRequest: RequestSchema<{ enabled: boolean }> = {
  name: 'releasePushStatus',
  input: '/api/v1/deviceToken',
  method: 'GET',
};

/** Format the email-warning suppression ISO date as a short human date ('' if unparseable). */
function formatSuppressedDate(iso?: string | null): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

enum MediumFast {
  // Just started and have WS connected
  WS_CONNECTED_START = 1000,
  // Just found a message over non-WS and WS connected
  WS_CONNECTED_JUST_RECEIVED = 2000,
  // No data for a long time and WS connected
  WS_CONNECTED_MAX = 30000,
  // Out-of-business hours
  WS_CONNECTED_MAX_OOB = 200000,
  // Increase after each check by this amount
  WS_CONNECTED_INCREMENT = 1001,
  START = 999,
  JUST_RECEIVED = 2500,
  MAX = 11000,
  // Out-of-business hours
  MAX_OOB = 60000,
  INCREMENT = 500,
  ERROR_INCREMENT = 5000,
}

// The live-release cadence. Used while a release is live or about to start
// (see `updateSpeed`), on every platform, so a message missed on a
// still-connected socket self-heals within a few seconds instead of sitting
// unrendered until the next WS event (#1659). The WS is still the primary
// channel; this bounds how long a dropped frame can stay invisible. The MAX
// caps are deliberately low (a few seconds) but non-zero to keep the safety-net
// poll cheap even across a large live audience — and they only apply inside the
// scoped live window, which ends the moment "Done for now" is seen.
enum VeryFast {
  // Just started and have WS connected
  WS_CONNECTED_START = 1000,
  // Just found a message over non-WS and WS connected
  WS_CONNECTED_JUST_RECEIVED = 1001,
  // No data for a long time and WS connected
  WS_CONNECTED_MAX = 5000,
  // Out-of-business hours — a live release matters regardless of the hour, so
  // stay fast; only a touch gentler than in-hours.
  WS_CONNECTED_MAX_OOB = 8000,
  // Increase after each check by this amount
  WS_CONNECTED_INCREMENT = 300,
  START = 999,
  JUST_RECEIVED = 2500,
  MAX = 2501,
  // Out-of-business hours
  MAX_OOB = 8000,
  INCREMENT = 200,
  ERROR_INCREMENT = 5000,
}

function MessageReceiptListContent() {
  const currentUser = useMe();
  const memberships = useMemberships({ mergeSame: true, omitEmpty: false });
  const intl = useLocale();

  // Reading the Release Center drops ONLY the release-center contribution to the
  // PWA app-icon badge, not the whole badge (#1212). The sidebar's release-center
  // unread counter (Nav.tsx / WebSocketUnreadCounter) already zeroes its own
  // source when this route is active, and the shared aggregator (appBadge.ts)
  // re-sums the remaining sources — so an unread support chat keeps the badge at
  // its count. The old blanket clearAppBadge() here wiped every source at once
  // (support chat included), which is exactly the "opening the release center
  // cleared a badge whose other items were still unread" bug; removed.

  // "Don't miss a release" notice: shown ONLY to members with NO reliable push
  // channel — no live native push AND no Telegram — so anyone already reachable
  // never sees it. Hidden until both statuses have loaded so we never flash it at
  // connected users (a failed status fetch stays hidden — fail-safe under-nag).
  // The copy adapts across two cases in ONE banner: a general reliability nudge
  // (default) and a dead email warning channel (meta['sg:warningsSuppressed'],
  // set by the SendGrid webhook — #1383); the email case takes priority when both
  // apply. Broad app awareness lives in the sidebar (AppPromoCard); this banner is
  // the urgent, contextual nudge.
  const telegramStatus = useRequest(telegramStatusRequest, { manual: true });
  const pushStatus = useRequest(pushStatusRequest, { manual: true });
  useEffect(() => {
    telegramStatus.run().catch(() => undefined);
    pushStatus.run().catch(() => undefined);
  }, []);
  const channelsLoaded = !!telegramStatus.data && !!pushStatus.data;
  const hasReliableChannel = !!telegramStatus.data?.connected || !!pushStatus.data?.enabled;
  const isNative = isNativePlatform();
  // Never in the native app: it already is the reliable channel (push), and the
  // banner only took feed space there (#2021).
  const showDeliveryNotice = channelsLoaded && !hasReliableChannel && !isNative;
  const emailWarningsSuppressed = !!currentUser?.meta?.['sg:warningsSuppressed'];
  const emailSuppressedDate = formatSuppressedDate(
    currentUser?.meta?.['sg:warningsSuppressed']?.at,
  );
  const scrollContainer = useRef<HTMLElement>(null);
  const loadOlderRef = useRef<HTMLButtonElement>(null);
  const scrollAnchorRef = useRef<{ id: number; top: number } | null>(null);
  const [isAutoLoading, setIsAutoLoading] = useState(false);

  const userRef = useRef(currentUser);

  if (userRef.current !== currentUser) {
    userRef.current = currentUser;
  }

  const isStaff = currentUser?.role === 'staff' || currentUser?.role === 'admin';
  const isAdmin = currentUser?.role === 'admin';

  // A grade/settle burst delivers many `messageReceipt/updated` events at once.
  // Each event needs the receipt's fresh row, but fetching them one GET-per-id
  // fanned out N requests and 503'd the pool (#1801). Coalesce the ids arriving
  // within a short window into ONE `id$in` request, then hand each event its own
  // row from the batch. `messageReceipt/created` events already carry full data,
  // so only `updated` fetches.
  const receiptBatcherRef = useRef(
    createBatcher<number, Map<number, API.MessageReceipt>>(
      async (ids) => {
        const withMeta = userRef.current?.role === 'admin' || userRef.current?.role === 'staff';
        const res = await request<{ data: API.MessageReceipt[] }>('/api/v1/messageReceipt/me', {
          query: {
            data: { id$in: ids },
            page: { limit: Math.min(Math.max(ids.length, 1), 100) },
            ...(withMeta ? { withMessageMeta: true } : {}),
          },
        });
        const byId = new Map<number, API.MessageReceipt>();
        for (const receipt of res?.data || []) {
          byId.set(receipt.id, receipt);
        }
        return byId;
      },
      // 50ms batch window, plus up to 450ms of per-client jitter (#2059). The
      // window opens on a broadcast `messageReceipt/updated`, so without jitter
      // every client's batch flushed inside the same 50ms. This never delays the
      // pick itself: `messageReceipt/created` carries full data and never batches.
      50,
      450,
    ),
  );

  const eventValidate = useCallback(
    async (
      _event: MessageEvent,
      ojson: API.SubscriptionResponse<API.MessageReceipt> | API.WebsocketResult<API.Id>,
    ) => {
      if (ojson.message === 'messageReceipt/updated') {
        const id = ojson.data as number;
        const byId = await receiptBatcherRef.current.add(id);
        const data = byId.get(id);

        // The row may be absent from the batch (e.g. now-excluded / no longer
        // visible to this member). Drop the event rather than injecting nothing.
        if (!data) {
          return false;
        }

        return {
          ...ojson,
          data,
        } satisfies API.SubscriptionResponse<API.MessageReceipt>;
      }
    },
    [],
  );

  const sendMessageRequest = useRequest(messageCreateRequest, { manual: true });
  const { run: sendMessage } = sendMessageRequest;

  const onCopy: ClipboardEventHandler<HTMLParagraphElement> = useCallback(async () => {
    if (isStaff) {
      return; // skip for staff/admin
    }
    try {
      const selectedText = document.getSelection()?.toString().trim();

      if (selectedText) {
        await sendMessage({
          data: {
            content: selectedText,
            receiverUserId: '0',
            type: 'dmh',
          },
        });
      }
    } catch (error) {
      if (import.meta.env.PROD) {
        console.error(error);
      }
    }
  }, [isStaff, sendMessage]);

  const [PollingDelays, setPollingDelays] = useState<
    typeof MediumFast | typeof VeryFast | undefined
  >(undefined);

  const {
    isPending,
    isSuccess,
    error,
    data: results,
    isFetching,
    isFetchingNextPage,
    // isFetchingPreviousPage,
    fetchNextPage: loadOlder,
    // fetchPreviousPage,
    hasNextPage,
    // hasPreviousPage,
    wsConnected,
    newestItem,
    oldestItem,
    renderPayload,
  } = useNextInfiniteRequest<
    API.MessageReceipt,
    void,
    API.WithPageInputDataless<{ withMessageMeta: boolean }>
  >(
    useMemo(
      () => ({
        key: 'fetchMessages',
        req: `/api/v1/messageReceipt/me`,
        query:
          userRef.current?.role === 'admin' || userRef.current?.role === 'staff'
            ? { withMessageMeta: true }
            : undefined,
        event: 'messageReceipt',
        eventValidate,
        // serverSkip: true,
        // skipPoll: true,
        PollingDelays: PollingDelays || PollingDelaysDefault,
      }),
      [PollingDelays, eventValidate],
    ),
  );

  // Choose the safety-net polling cadence from the current release-window state.
  // The scan mirrors LiveNowIndicator's newest-first state machine: while a
  // release is LIVE or about to start we poll fast on EVERY platform (#1659) so
  // a message missed on a still-connected socket self-heals within seconds; the
  // window ends the moment "Done for now" is seen, dropping back to the default
  // cadence. A recent-but-not-live feed keeps the medium cadence on mobile only
  // (a just-backgrounded phone still catches up reasonably), and desktop rests
  // at the default backoff.
  const updateSpeed = useMemo(() => {
    if (!results?.data || !results?.data.length) {
      return undefined;
    }

    const platform = `${
      typeof navigator !== 'undefined' && (navigator.userAgentData?.platform || navigator.platform)
    }`;
    const isIosOrAndroid = platform.startsWith('i') || platform.toLowerCase().includes('android');

    const now = Date.now();

    // Check the last 50 messages
    for (let i = 0; i < 50; i++) {
      const receipt = results.data[i];

      if (!receipt) {
        break;
      }

      const createdAt = Date.parse(receipt.createdAt);
      const { content, release, notify } = receipt.message;

      if (now - createdAt > 60 * 60 * 4 * 1000) {
        // too old
        break;
      }

      // Release ended — "Done for now" / "No more releases". Back off to the
      // default cadence; the fast poll is no longer needed.
      if (/^\W*(Done|No more releases)\W*([.!]*|for )/i.test(content)) {
        return undefined;
      }

      // Live window — a play is out (a release, a "Set up", or a bare "GO"), or a
      // warning says one is imminent. Poll fast on every platform.
      if (
        release ||
        notify === 'releaseWarning' ||
        /^Set up/i.test(content) ||
        /^\W*GO\W*[.!]*$/i.test(content) ||
        /^\W*(We will start in|We will get started in|Will get started in|Will get started shortly|Next prop in [0-9]+ (?:seconds|minutes)|Here we go\.\.\.|About to start)\W*([.!]*|for )/i.test(
          content,
        )
      ) {
        return VeryFast;
      }

      // A recent, non-live message: keep a medium cadence on mobile (a
      // just-backgrounded phone still catches up), default cadence on desktop.
      return isIosOrAndroid ? MediumFast : undefined;
    }
  }, [results, results?.data.length, results?.data?.[0]]);

  useEffect(() => {
    setPollingDelays(updateSpeed);
  }, [updateSpeed]);

  const lowCreditsMemberships: API.Membership<true>[] = [];
  const emptyCreditsMemberships: API.Membership<true>[] = [];
  let _noMemberships = false;

  if (!memberships.isPending && memberships.data?.data) {
    _noMemberships = true;

    for (const membership of memberships.data.data) {
      const warningCount =
        typeof membership.product?.stats?.buyable === 'number'
          ? Math.min(10, Math.max(membership.remaining, membership.product.stats.buyable))
          : 5;

      if (
        warningCount >= 1 &&
        membership.remaining < warningCount &&
        !membership.bonusId &&
        ![44, 51, 52, 59].includes(membership.productId)
      ) {
        if (membership.remaining === 0) {
          emptyCreditsMemberships.push(membership);
        } else {
          lowCreditsMemberships.push(membership);
        }
      }

      if (membership.remaining > 0) {
        _noMemberships = false;
      }
    }
  }

  const isEmpty = !newestItem && !oldestItem;

  const doScroll = useCallback((selector: string, top?: boolean, scrollTopLimit?: number) => {
    const el = scrollContainer.current?.querySelector(selector);

    if (top) {
      scrollContainer.current?.scrollTo({ top: 1 });
    }

    el?.scrollIntoView?.({ block: 'start' });

    setTimeout(() => {
      const scrollTop = Math.abs(scrollContainer.current?.scrollTop || 0);

      if (scrollTopLimit && scrollTop > scrollTopLimit) {
        // make sure user hasn't scrolled away; this prevents jitter
        return;
      }

      const el2 = scrollContainer.current?.querySelector(selector);

      if (top) {
        scrollContainer.current?.scrollTo({ top: -1 });
      }

      el2?.scrollIntoView?.({ block: 'start' });
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previousOldestId = useRef(oldestItem?.id);
  const previousNewestId = useRef(newestItem?.id);

  useEffect(() => {
    if (!scrollContainer.current || !results?.data.length) {
      return;
    }

    const scrollTop = Math.abs(scrollContainer.current.scrollTop);

    if (newestItem?.id && newestItem.id !== previousNewestId.current) {
      // New entry, scroll to end
      previousNewestId.current = newestItem.id;
      // But also update oldest to prevent scroll on next check
      previousOldestId.current = oldestItem?.id;

      doScroll(`[data-receiptid="${parseInt(`${newestItem.id}`, 10)}"]`, true);
    } else if (oldestItem?.id && oldestItem.id !== previousOldestId.current) {
      // Older messages loaded — restore scroll position using saved anchor
      previousOldestId.current = oldestItem.id;

      if (scrollAnchorRef.current && scrollContainer.current) {
        const { id, top: savedTop } = scrollAnchorRef.current;
        const el = scrollContainer.current.querySelector(`[data-receiptid="${id}"]`);

        if (el) {
          const delta = el.getBoundingClientRect().top - savedTop;
          scrollContainer.current.scrollTop += delta;
        }

        scrollAnchorRef.current = null;
      }
    } else {
      // Possibly an update, scroll to end if already scrolled almost to end
      if (scrollTop < 250 && scrollTop > 0 && newestItem?.id) {
        doScroll(`[data-receiptid="${parseInt(`${newestItem.id}`, 10)}"]`, true, 250);
      }
    }
  }, [oldestItem?.id, newestItem?.id, results]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      setIsAutoLoading(false);
    }
  }, [isFetchingNextPage]);

  let metaMessage = '';

  if (currentUser?.meta?.emailUnverified) {
    metaMessage = 'Your email address is currently unverified.';
  } else if (
    currentUser?.meta?.email &&
    currentUser.meta.email.toLowerCase() !== (currentUser.email || '').toLowerCase()
  ) {
    metaMessage =
      'Your email address change has not yet been confirmed. Emails will continue to go to your old address.';
  } else if (
    (currentUser?.meta?.contactSecondary && !currentUser.contactSecondary) ||
    (currentUser?.meta?.contactTertiary && !currentUser.contactTertiary)
  ) {
    metaMessage = 'Your additional contact methods have not yet been confirmed for use.';
  } else if (currentUser?.meta?.contactSecondary || currentUser?.meta?.contactTertiary) {
    metaMessage = 'Your additional contact methods change has not yet been confirmed.';
  }

  const isDesktop = useMediaQuery('(min-width: 640px)');
  const isPwa = useMediaQuery('(display-mode: standalone)');
  const isMobileOrApp = !isDesktop || isPwa;

  const { promptType, promptToInstall, isInstalled, hideAppPromptTime, setHideAppPromptTime } =
    useAddToHomescreenPrompt();

  const setHideAppPromptToNow = useCallback(() => {
    setHideAppPromptTime(Date.now());
  }, [setHideAppPromptTime]);

  const isAppPromptHidden = useMemo(
    () => (hideAppPromptTime ? +hideAppPromptTime : 0) + 7 * 24 * 60 * 60 * 1000 > Date.now(),
    [hideAppPromptTime],
  );

  const updateMessageRequest = useMemo<
    RequestSchema<API.Message, { data: { meta: { flags?: string[]; note?: string | null } } }>
  >(
    () => ({
      input: `/api/v1/admin/message/update/:id`,
      method: 'POST',
    }),
    [],
  );
  const updateRequest = useRequest(
    updateMessageRequest,
    useMemo(() => ({ manual: true }), []),
  );

  return (
    <>
      {/* The iOS shell already insets content below the status bar (capacitor
          .config `contentInset: 'always'`), so this feed header no longer
          hand-rolls its own `pt` — stacking a second env() inset over-spaced it.
          It sits at the top of <main>, outside the feed's scroll container, so it
          stays pinned under the status bar with the title below the clock (#1573). */}
      <Header title="Release Center" Icon={MegaphoneIcon} className="py-2">
        <div className="mr-auto ml-2 self-center">
          <SoundPlayer
            text={
              isMobileOrApp
                ? 'No warning sounds. Click to enable.'
                : "You're not receiving warning sounds. Click anywhere to enable."
            }
            small
          />
        </div>
        <LiveNowIndicator receipts={results?.data} />
        <ConnectionStatusIcon />
      </Header>
      {/* Pinned pointer to entitled pending picks (ghost-aware). Shows whenever
          the member has pending picks — even with a non-empty feed — since
          receipt-gated surfaces here never show picks released before they
          subscribed (#1445). Self-clears when the count hits 0. */}
      <PendingPicksNotice />
      {error && (
        <aside className="bg-amber-50 p-4">
          <div className="flex flex-row items-center space-x-2 text-amber-700 text-sm">
            <ExclamationCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>Error: {error.message}</span>
          </div>
        </aside>
      )}
      {!!currentUser?.paused && (
        <aside className="bg-blue-50 p-4">
          <div className="flex flex-row items-center space-x-2 text-blue-700 text-sm">
            <PauseCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>
              You have paused your account.{' '}
              <a
                href="/settings/memberships"
                className="text-blue-700 underline hover:text-blue-600"
              >
                Unpause your account to receive new releases.
              </a>
            </span>
          </div>
        </aside>
      )}
      {!!metaMessage && (
        <aside className="bg-blue-50 p-4">
          <div className="flex flex-row items-center space-x-2 text-blue-700 text-sm">
            <PauseCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>
              {metaMessage}{' '}
              <a href="/settings" className="text-blue-700 underline hover:text-blue-600">
                Visit your account settings to change.
              </a>
            </span>
          </div>
        </aside>
      )}
      {/* {noMemberships && (
          <aside className="bg-amber-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-amber-700">
              <ExclamationCircleIcon className="h-6 w-6 shrink-0" aria-hidden="true" />
              <span>
                You don't have any active services
                {currentUser?.tags?.includes('free') ? ', apart from free plays' : ''}.{' '}
                <a href="/order" className="text-amber-700 underline hover:text-amber-600">
                  Order now
                </a>{' '}
                to avoid missing any plays!
              </span>
            </div>
          </aside>
        )} */}
      {/* "Don't miss a release" notice — shown to members with no reliable push
          channel (no native push AND no Telegram), steering them to a reliable
          one (app / Telegram):
            • email warning channel dead (meta['sg:warningsSuppressed'], #1383) —
              takes priority; calls out that email warnings are off.
            • otherwise a general reliability nudge.
          Consolidates what were separate email-warnings-off and Android-install
          banners (#1383). SMS was retired; that expectation-setting now lives in
          the notification emails, so this copy is forward-looking (no SMS). The
          app CTA is hidden inside the native app (they already have it). */}
      {showDeliveryNotice && (
        <Alert>
          <div className="flex flex-row items-start space-x-2 text-amber-800 text-sm">
            {emailWarningsSuppressed ? (
              <BellAlertIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            ) : (
              <DevicePhoneMobileIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            )}
            <div className="space-y-3">
              {emailWarningsSuppressed ? (
                <span className="block">
                  <strong>
                    Your email release warnings are off
                    {emailSuppressedDate ? ` (unsubscribed ${emailSuppressedDate})` : ''}.
                  </strong>{' '}
                  {isNative ? 'Connect Telegram' : 'Get the app or connect Telegram'} so you
                  don&apos;t miss plays.
                </span>
              ) : (
                <span className="block">
                  <strong>Don&apos;t miss a release.</strong> To get notified the moment picks drop,{' '}
                  {isNative ? 'connect Telegram' : 'get the app, connect Telegram,'} or keep your
                  email up to date.
                </span>
              )}
              {/* Promote the reliable push channels — the mobile app first, then
                  Telegram. "Get the app" opens the /app landing page (store links
                  + Telegram, iOS "coming soon"); hidden inside the native app. */}
              <div className="flex flex-wrap gap-2">
                {!isNative && (
                  <a
                    href="/app"
                    className="inline-flex items-center rounded-md bg-amber-600 px-2.5 py-1.5 font-semibold text-white text-xs transition hover:bg-amber-700"
                  >
                    Get the app
                  </a>
                )}
                <a
                  href="/settings?scrollTo=telegram"
                  className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 font-semibold text-amber-800 text-xs ring-1 ring-amber-300 transition hover:bg-amber-100"
                >
                  Connect Telegram
                </a>
                {!emailWarningsSuppressed && (
                  <a
                    href="/settings?scrollTo=email"
                    className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 font-semibold text-amber-800 text-xs ring-1 ring-amber-300 transition hover:bg-amber-100"
                  >
                    Update email
                  </a>
                )}
              </div>
            </div>
          </div>
        </Alert>
      )}
      {!!lowCreditsMemberships.length && (
        <Alert>
          <div className="flex flex-row items-start space-x-2 text-amber-700 text-sm">
            <ExclamationCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>
              You&apos;re almost out of play credits on{' '}
              {lowCreditsMemberships.length === 1 ? 'one' : 'some'} of your services.
              {!isIosNative() && (
                <>
                  {' '}
                  <a href="/order" className="text-amber-700 underline hover:text-amber-600">
                    Add more
                  </a>{' '}
                  to avoid missing any plays!
                </>
              )}
            </span>
          </div>
        </Alert>
      )}
      {!!emptyCreditsMemberships.length && (
        <Alert color="red">
          <div className="flex flex-row items-start space-x-2 text-red-700 text-sm">
            <ExclamationCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>
              You&apos;ve run out of play credits on{' '}
              {emptyCreditsMemberships.length === 1 ? 'one' : 'some'} of your services.
              {!isIosNative() && (
                <>
                  {' '}
                  <a href="/order" className="text-red-700 underline hover:text-red-600">
                    Add more
                  </a>{' '}
                  to avoid missing any plays!
                </>
              )}
            </span>
          </div>
        </Alert>
      )}
      {(promptType === 'app' || promptType === 'safari:macos') &&
        !!promptToInstall &&
        !isInstalled &&
        !isAppPromptHidden && (
          <Alert color="black" onClose={setHideAppPromptToNow}>
            <div className="flex select-none flex-row items-start space-x-2 text-sm text-white">
              <SparklesIcon
                height={24}
                className="size-6 shrink-0 text-amber-300"
                aria-hidden="true"
              />
              <div className="flex flex-col items-start space-y-4">
                <span>
                  RAS now has a desktop app featuring more reliable message sounds and
                  notifications.
                </span>
                {promptType === 'app' ? (
                  <button
                    className="inline-flex items-center space-x-2 rounded-sm px-1.5 py-2 text-amber-300 text-xs ring-1 ring-amber-300 hover:bg-amber-300 hover:text-zinc-900"
                    onClick={promptToInstall}
                  >
                    <ArrowDownTrayIcon height={20} className="size-5 shrink-0" />
                    <span>Install RAS desktop app</span>
                  </button>
                ) : promptType === 'safari:macos' ? (
                  <span className="inline-flex items-center text-amber-300 text-xs">
                    Click&nbsp;
                    <ArrowUpOnSquareIcon height={20} className="size-5 shrink-0" />
                    &nbsp;in the top toolbar, then choose Add to Dock.
                  </span>
                ) : (
                  <span className="inline-flex items-center text-amber-300 text-xs">
                    Click&nbsp;
                    <ArrowUpOnSquareIcon height={20} className="size-5 shrink-0" />
                    &nbsp;in the bottom toolbar, then choose Add to Home Screen&nbsp;
                    <PlusCircleIcon height={20} className="size-5 shrink-0" />
                  </span>
                )}
              </div>
            </div>
          </Alert>
        )}
      {!!error && !wsConnected && (
        <aside className="bg-red-50 p-4">
          <div className="flex flex-row items-center space-x-2 text-red-700 text-sm">
            <ExclamationCircleIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
            <span>There seems to be a problem loading messages. We'll keep trying...</span>
          </div>
        </aside>
      )}
      {/* {!isPending && !wsConnected && (
          <aside className="bg-yellow-50 p-4">
            <div className="flex flex-row items-center space-x-2 text-sm text-yellow-700">
              <SignalSlashIcon className="h-6 w-6" />
              <span>Degraded connection. Messaging service may be delayed.</span>
            </div>
          </aside>
        )} */}
      {isPending && <SkeletonMessages />}
      {isSuccess && isEmpty && !error && (
        <aside className="mx-auto flex h-[stretch] w-[stretch] grow items-center justify-center overflow-y-auto px-6 py-10 text-center">
          <div className="my-auto flex w-full max-w-sm flex-col items-center gap-5">
            <div className="flex size-16 items-center justify-center rounded-full bg-[#15734A]/10 text-[#15734A] dark:bg-[#34B473]/15 dark:text-[#34B473]">
              <InboxIcon className="size-8 stroke-[1.5]" />
            </div>
            <div className="space-y-2">
              <h2 className="font-semibold text-xl text-zinc-900 dark:text-white">
                You haven't received any releases yet
              </h2>
              <p className="text-sm text-zinc-500 leading-6 dark:text-zinc-400">
                Thanks for joining us,{' '}
                <span className="font-semibold text-zinc-700 dark:text-zinc-200">
                  {currentUser?.firstName}
                </span>
                ! This is where all plays will be released. If you've recently subscribed to a
                service, wait for a release warning and return here.
              </p>
            </div>
            <ul className="w-full space-y-3 rounded-xl bg-zinc-900/[0.03] p-4 text-left text-sm text-zinc-600 dark:bg-white/5 dark:text-zinc-400">
              <li className="flex items-start gap-2.5">
                <ClockIcon className="mt-0.5 size-5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                <span>Only new releases will be delivered here.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <EyeSlashIcon className="mt-0.5 size-5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                <span>Previously released plays will not be shown here.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <ShoppingCartIcon className="mt-0.5 size-5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                <span>You need to order a service to receive paid releases.</span>
              </li>
            </ul>
            {/* Web checkout link: hidden in the iOS shell (App Store 3.1.1). */}
            {!isIosNative() && (
              <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-1 text-sm text-zinc-700 dark:text-zinc-300">
                <span>Still need to order a service?</span>
                <a
                  href="/order"
                  className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400"
                >
                  <span>Order here</span>
                  <ArrowRightIcon height={16} className="size-4" />
                </a>
              </div>
            )}
          </div>
        </aside>
      )}
      {!isSuccess && !results && !isPending && (
        <>Sorry, something seems to be wrong with our servers at the moment.</>
      )}
      {!isEmpty && (
        <div
          className={twMerge(
            'relative flex min-h-0 grow flex-col',
            // Staff on touch: the long-press opens the row action menu, so make
            // the WHOLE feed non-selectable. Guarding only the pressed row is not
            // enough on iOS/WebKit — a touch-and-hold on a `user-select: none`
            // element selects the nearest selectable ANCESTOR instead, i.e. the
            // whole feed / screen (#2021). Members keep selection (their copy
            // handler lives on the list below); desktop is unaffected (no-hover).
            isStaff &&
              'no-hover:select-none no-hover:[-webkit-touch-callout:none] no-hover:[-webkit-user-select:none]',
          )}
        >
          {/* Top fade band. The opaque header above already fills the iOS notch /
              status-bar band (safe-area-inset-top, #1461); this soft gradient sits
              just below it, pinned to the top of the feed (it's on this relative
              wrapper, NOT inside the scroll container, so it never scrolls) and
              fades the newest content cleanly as it scrolls up under the header,
              mirroring the member end-of-feed fade at the bottom (#1461-1463).
              Mobile-only — desktop keeps the flat feed. from-(--background) => the
              page/header ground, so it reads as content dissolving, not a bar. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-linear-to-b from-(--background) to-transparent sm:hidden"
          />
          <section
            className="flex h-fit grow flex-col-reverse overflow-y-auto"
            style={{ overflowAnchor: 'none' }}
            ref={scrollContainer}
          >
            {/* Mobile bottom padding. Staff/admin clear the floating compose
              capsule (MessageInput, sm:hidden): its height plus the gap it floats
              above the iOS home-indicator inset, so the newest message sits right
              above it and nothing hides under it.
              (This was 5rem for the old floating round button; that clearance
              read as a blank white band under the feed, #2028.) The read-only
              member view has no tab and ends in the "end of feed" article below,
              which carries its OWN safe-area inset so the fade stays continuous
              to the home indicator — putting the inset here instead would render
              a bare white strip below that gradient (#1461 follow-up).
              var(--safe-bottom) is 0 on web, the home indicator on iOS, and the
              gesture-bar inset on Android (#2098). */}
            <div
              className={`flex flex-col pt-4 ${
                isStaff ? 'max-sm:pb-[calc(3rem+var(--safe-bottom))]' : ''
              }`}
              onCopy={!isStaff ? onCopy : undefined}
            >
              {hasNextPage && (
                <aside className="text-center">
                  <InView
                    as="aside"
                    className="text-center"
                    rootMargin="600px 0px 0px 0px"
                    onChange={(inView, _entry) => {
                      if (
                        inView &&
                        isSuccess &&
                        !isFetching &&
                        !isFetchingNextPage &&
                        loadOlderRef.current
                      ) {
                        // Save the first visible element's position before loading
                        // so we can restore scroll position after new items render
                        const firstVisible = scrollContainer.current?.querySelector(
                          '[data-receiptid]',
                        ) as HTMLElement | null;

                        if (firstVisible && scrollContainer.current) {
                          scrollAnchorRef.current = {
                            id: parseInt(firstVisible.dataset.receiptid || '0', 10),
                            top: firstVisible.getBoundingClientRect().top,
                          };
                        }

                        setIsAutoLoading(true);
                        loadOlderRef.current.click();
                      }
                    }}
                  ></InView>
                  <button
                    disabled={isFetchingNextPage}
                    ref={loadOlderRef}
                    onClick={() => loadOlder()}
                    className="m-0 cursor-pointer select-none border-0 bg-transparent p-2 text-xs text-zinc-400 hover:text-zinc-500 dark:text-zinc-500 dark:hover:text-zinc-400"
                  >
                    {isAutoLoading || isFetchingNextPage ? (
                      <LoadingIcon isLoading={true} className="h-4 w-4 opacity-50" />
                    ) : (
                      'Load older messages'
                    )}
                  </button>
                </aside>
              )}
              {renderPayload((payload) => {
                const receipts = [...payload.data].reverse();

                return receipts.map((props, receiptIndex) => {
                  const receiptTime = Date.parse(props.createdAt);
                  const olderReceipt = receipts[receiptIndex - 1];

                  const date = intl.formatDate(props.createdAt, {
                    month: 'long',
                    day: 'numeric',
                    weekday: 'short',
                  });

                  let showDate = true;
                  let showUserInfo = true;

                  if (olderReceipt) {
                    const olderReceiptTime = Date.parse(olderReceipt.createdAt);
                    const olderDate = intl.formatDate(olderReceipt.createdAt, {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'short',
                    });

                    if (olderDate === date) {
                      showDate = false;

                      if (receiptTime - olderReceiptTime <= 5 * 60 * 1000) {
                        showUserInfo = false;
                      }
                    }
                  }

                  // Admin-only: hand the Create Release form the feed messages
                  // just above this one (chronological) so it can resolve the
                  // units rating from a directive stated before the "Set up" line
                  // ("First play for 1.5 units") (#1211). The resolver bounds its
                  // own lookback to the current play block; a small window keeps
                  // the per-message work cheap. Skipped for non-admins (no form).
                  const precedingMessages = isAdmin
                    ? receipts
                        .slice(Math.max(0, receiptIndex - 40), receiptIndex)
                        .map((r) => ({ content: r.message?.content, createdAt: r.createdAt }))
                    : undefined;

                  // Admin-only, "Set up" lines only: the feed messages just BELOW
                  // this one, so the Create Release form can auto-detect the GO /
                  // CANCEL / OPPOSITE follow-up that resolves it (the "Set Up & Go"
                  // flow). Scoped to set-ups so the memo fingerprint (and this
                  // slice) does no work for the vast majority of messages that
                  // never open a confirmation form. Updates live as new messages
                  // land (the follow-up arrives ~15-25s later).
                  const followingMessages =
                    isAdmin && isSetupMessage(props.message?.content)
                      ? receipts
                          .slice(receiptIndex + 1, receiptIndex + 13)
                          .map((r) => ({ content: r.message?.content, createdAt: r.createdAt }))
                      : undefined;

                  const retjsx = (
                    <React.Fragment key={props.id}>
                      {showDate && (
                        <article className="my-4 flex items-center px-4">
                          <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                          <time className="shrink-0 px-2 text-xs text-zinc-900/50 dark:text-zinc-500">
                            {date}
                          </time>
                          <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                        </article>
                      )}

                      {props.release ? (
                        <Message
                          receipt={props}
                          userInfo={showUserInfo}
                          updateRequest={updateRequest}
                          isAdmin={isAdmin}
                          showNotify={isStaff}
                          precedingMessages={precedingMessages}
                          followingMessages={followingMessages}
                        >
                          <Release release={props.release} />
                        </Message>
                      ) : (
                        <Message
                          receipt={props}
                          userInfo={showUserInfo}
                          updateRequest={updateRequest}
                          isAdmin={isAdmin}
                          showNotify={isStaff}
                          precedingMessages={precedingMessages}
                          followingMessages={followingMessages}
                        />
                      )}

                      <ErrorBoundary>
                        <Suspense>
                          <MessageReceiptLogger {...props} />
                        </Suspense>
                      </ErrorBoundary>
                    </React.Fragment>
                  );

                  return retjsx;
                });
              })}
              {!isStaff && (
                // The member "end of feed" divider. The gradient fade is desktop-
                // only (sm+) — on mobile it read as an out-of-place grey/white bar,
                // so mobile shows just the divider with tighter vertical padding.
                // It still carries the iOS home-indicator inset as bottom padding
                // so content clears the home bar (env(...,0px) => web/Android 0).
                <article className="sm:bg-linear-to-b sm:from-transparent sm:to-zinc-100 max-sm:pb-[var(--safe-bottom)] sm:dark:to-black">
                  <div className="flex items-center px-4 py-4 max-sm:py-2">
                    <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                    <MegaphoneIcon className="h-4 w-auto shrink-0 px-2 text-zinc-400 dark:text-zinc-600" />
                    <hr className="h-px grow border-0 bg-zinc-900/10 dark:bg-white/10" />
                  </div>
                </article>
              )}
            </div>
          </section>
        </div>
      )}
      {/* Show a little spinner in case we HAVE messages but are also re-fetching */}
      {!isPending && isFetching && (
        <div className="pointer-events-none relative opacity-25">
          <SkeletonMessages className="absolute z-10" />
        </div>
      )}
    </>
  );
}

export default function MessageReceiptList() {
  const currentUser = useMe();
  const isStaff = currentUser?.role === 'staff' || currentUser?.role === 'admin';

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <ErrorBoundary>
        <Suspense fallback={<SkeletonMessages />}>
          <MessageReceiptListContent />
        </Suspense>
      </ErrorBoundary>

      {isStaff && (
        <ClientOnly component={() => import('./MessageInput')}>
          <MessageInputSkeleton />
        </ClientOnly>
      )}
    </div>
  );
}
